import { type BoxProps } from '@chakra-ui/react'
import { Button, Flex, Grid, GridItem, Text } from '@chakra-ui/react'
import { type ConditionalValue } from '@chakra-ui/react'
import React, { useRef } from 'react'
import { useIsInActiveView } from '~/hooks/useIsInActiveView'
import { useKeyPresses } from '~/hooks/useKeyboard'
import { usePlayDTMF } from '~/hooks/usePlayDTMF'
import { Size } from '~/types/ThemeTypes'

import { useKeypadStyles } from './componentStyles/Keypad.style'
import {
  hasModifierKey,
  isKeyTargetOutsideKeypad,
  markHandledByKeypad,
} from './utils/keyboardUtils'
import { isKeypadButton } from './utils/stringUtils'

const EMPTY_SECONDARY_CHARACTERS = '\u200E'

interface KeypadButtonProps {
  digit: string
  characters?: string
  onClick: (digit: string) => void
  isActive?: boolean
  styles: ReturnType<typeof useKeypadStyles>
}
const KeypadButton = ({
  digit,
  characters,
  onClick,
  isActive,
  styles,
}: KeypadButtonProps) => {
  const handleClick = () => {
    onClick(digit)
  }

  // Extract layout properties to center button in grid cell (Chakra v3 requirement)
  const containerLayoutProps = {
    display: styles.buttonContainer?.display,
    flexDirection: styles.buttonContainer?.flexDirection,
    justifyContent: styles.buttonContainer?.justifyContent,
    alignItems: styles.buttonContainer?.alignItems,
  }

  return (
    <GridItem
      css={styles.buttonContainer}
      {...containerLayoutProps}
      onClick={handleClick}
    >
      <Button data-active={isActive ? '' : undefined} css={styles.button}>
        <Text css={styles.primaryCharacter}>{digit}</Text>
        {characters ? (
          <Text css={styles.secondaryCharacters}>{characters}</Text>
        ) : (
          <Text css={styles.secondaryCharacters} aria-hidden="true">
            {EMPTY_SECONDARY_CHARACTERS}
          </Text>
        )}
      </Button>
    </GridItem>
  )
}

type KeypadProps = {
  onButtonClick: (digit: string) => void
  size?: ConditionalValue<Size.sm | Size.md | Size.lg>
  leftButton?: React.ReactElement
  centerButton?: React.ReactElement
  rightButton?: React.ReactElement
  muteDTMF?: boolean
} & Omit<BoxProps, 'direction'>

export const Keypad = ({
  onButtonClick,
  size,
  leftButton,
  centerButton,
  rightButton,
  muteDTMF = false,
  ...rest
}: KeypadProps) => {
  const keypadRef = useRef<HTMLDivElement>(null)
  const isInActiveView = useIsInActiveView(keypadRef.current)
  const playDTMF = usePlayDTMF()
  useKeyPresses({
    filter: (e) =>
      isInActiveView &&
      isKeypadButton(e.key) &&
      !hasModifierKey(e) &&
      !isKeyTargetOutsideKeypad(e.target, keypadRef.current),
    onPress: (e) => {
      e.preventDefault()
      markHandledByKeypad(e)
      handleDialButtonClick(e.key)
    },
  })

  const activeButtonsRef = useRef<Record<string, boolean>>({
    '1': false,
    '2': false,
    '3': false,
    '4': false,
    '5': false,
    '6': false,
    '7': false,
    '8': false,
    '9': false,
    '*': false,
    '0': false,
    '#': false,
  })

  const styles = useKeypadStyles({
    size,
  })

  const handleDialButtonClick = (key: string) => {
    onButtonClick(key)
    if (!muteDTMF) {
      playDTMF(key)
    }
    activeButtonsRef.current[key] = true

    setTimeout(() => {
      activeButtonsRef.current[key] = false
    }, 100)
  }

  // Extract grid layout properties from merged styles
  // These are now properly merged thanks to the fixed mergeMultiStyleConfig
  const gridProps = {
    display: 'grid' as const,
    gridTemplateColumns: styles.keypad?.gridTemplateColumns,
    gridAutoRows: styles.keypad?.gridAutoRows,
    gap: styles.keypad?.gap,
  }

  // Extract layout properties for button containers to center buttons in grid cells
  const buttonContainerLayoutProps = {
    display: styles.buttonContainer?.display,
    flexDirection: styles.buttonContainer?.flexDirection,
    justifyContent: styles.buttonContainer?.justifyContent,
    alignItems: styles.buttonContainer?.alignItems,
  }

  return (
    <Flex
      ref={keypadRef}
      direction={'column' as const}
      alignItems="center"
      w="100%"
      {...rest}
    >
      <Grid css={styles.keypad} w="fit-content" maxW="100%" {...gridProps}>
        <KeypadButton
          isActive={activeButtonsRef.current['1']}
          digit="1"
          onClick={handleDialButtonClick}
          styles={styles}
        />
        <KeypadButton
          isActive={activeButtonsRef.current['2']}
          digit="2"
          characters="ABC"
          onClick={handleDialButtonClick}
          styles={styles}
        />
        <KeypadButton
          isActive={activeButtonsRef.current['3']}
          digit="3"
          characters="DEF"
          onClick={handleDialButtonClick}
          styles={styles}
        />

        <KeypadButton
          isActive={activeButtonsRef.current['4']}
          digit="4"
          characters="GHI"
          onClick={handleDialButtonClick}
          styles={styles}
        />
        <KeypadButton
          isActive={activeButtonsRef.current['5']}
          digit="5"
          characters="JKL"
          onClick={handleDialButtonClick}
          styles={styles}
        />
        <KeypadButton
          isActive={activeButtonsRef.current['6']}
          digit="6"
          characters="MNO"
          onClick={handleDialButtonClick}
          styles={styles}
        />

        <KeypadButton
          isActive={activeButtonsRef.current['7']}
          digit="7"
          characters="PQRS"
          onClick={handleDialButtonClick}
          styles={styles}
        />
        <KeypadButton
          isActive={activeButtonsRef.current['8']}
          digit="8"
          characters="TUV"
          onClick={handleDialButtonClick}
          styles={styles}
        />
        <KeypadButton
          isActive={activeButtonsRef.current['9']}
          digit="9"
          characters="WXYZ"
          onClick={handleDialButtonClick}
          styles={styles}
        />

        <KeypadButton
          isActive={activeButtonsRef.current['*']}
          digit="*"
          onClick={handleDialButtonClick}
          styles={styles}
        />
        <KeypadButton
          isActive={activeButtonsRef.current['0']}
          digit="0"
          characters="+"
          onClick={handleDialButtonClick}
          styles={styles}
        />
        <KeypadButton
          isActive={activeButtonsRef.current['#']}
          digit="#"
          onClick={handleDialButtonClick}
          styles={styles}
        />

        {leftButton && (
          <GridItem
            gridColumn="1 / 2"
            css={styles.buttonContainer}
            {...buttonContainerLayoutProps}
          >
            {leftButton}
          </GridItem>
        )}
        {centerButton && (
          <GridItem
            gridColumn="2 / 3"
            css={styles.buttonContainer}
            {...buttonContainerLayoutProps}
          >
            {centerButton}
          </GridItem>
        )}
        {rightButton && (
          <GridItem
            gridColumn="3 / 4"
            css={styles.buttonContainer}
            {...buttonContainerLayoutProps}
          >
            {rightButton}
          </GridItem>
        )}
      </Grid>
    </Flex>
  )
}
